import { useState } from "react";
import type { FormEvent } from "react";

import { isAPIError } from "@/core/services";
import { useDialogForm, useToast } from "@/ds";
import { criarGrupoBase } from "@/modules/financeiro-configuracoes";
import { useChaveIdempotencia, useConfirmarDescarte } from "@/shared/hooks";
import { ERROS, TOASTS } from "../../base-dados.constants";
import {
  CODIGO_PARADA,
  NOME_MAXIMO,
  NOVO_GRUPO_FORM_ID,
  SUBGRUPOS_AJUDA_103,
  SUBGRUPOS_AJUDA_DOCAGEM,
  SUBGRUPOS_AJUDA_VAZIO,
  SUBGRUPOS_DO_103,
} from "./novo-grupo-dialog.constants";
import {
  NOVO_GRUPO_VAZIO,
  erroNome,
  erroTipos,
  codigosMarcados,
  marcou103,
  montarCorpo,
  tipoTravado,
  tiposElegiveis,
} from "./novo-grupo-dialog.form";
import type { NovoGrupoForm } from "./novo-grupo-dialog.form";
import type { NovoGrupoDialogProps, NovoGrupoDialogViewModel } from "./types-novo-grupo-dialog";

export function useNovoGrupoDialog({
  tiposObra,
  onClose,
  onConcluido,
}: NovoGrupoDialogProps): NovoGrupoDialogViewModel {
  const toast = useToast();
  const { comChave } = useChaveIdempotencia();
  const [duplicado, setDuplicado] = useState<string | null>(null);
  const elegiveis = tiposElegiveis(tiposObra);

  const form = useDialogForm<NovoGrupoForm>({
    initial: NOVO_GRUPO_VAZIO,
    validate: ({ nome, tiposObraIds }) => ({
      nome: erroNome(nome) ?? undefined,
      tiposObraIds: erroTipos(tiposObraIds) ?? undefined,
    }),
    onSubmit: async (valores) => {
      const corpo = montarCorpo(valores);
      try {
        await comChave(corpo, (idempotencyKey) => criarGrupoBase(corpo, { idempotencyKey }));
      } catch (erro) {
        if (isAPIError(erro) && erro.status === 409) {
          const codigos = codigosMarcados(elegiveis, corpo.tiposObraIds);
          setDuplicado(erro.message || ERROS.duplicidade(corpo.nome, codigos.join(" e no ")));
          return;
        }
        throw erro;
      }
      const codigos = codigosMarcados(elegiveis, corpo.tiposObraIds);
      const { resumo, detalhe } = codigos.includes(CODIGO_PARADA)
        ? TOASTS.grupoAdicionado103(corpo.nome)
        : TOASTS.grupoAdicionadoDocagem(corpo.nome, codigos);
      toast.success(resumo, detalhe);
      onConcluido();
    },
  });

  const { values } = form;
  const descarte = useConfirmarDescarte({
    onDescartar: onClose,
    testId: "novo-grupo-descarte",
  });

  const alternarTipo = (id: string) => {
    setDuplicado(null);
    const marcados = values.tiposObraIds;
    form.setValue(
      "tiposObraIds",
      marcados.includes(id) ? marcados.filter((atual) => atual !== id) : [...marcados, id],
    );
  };

  const enviar = (evento: FormEvent<HTMLFormElement>) => {
    evento.preventDefault();
    void form.submit();
  };

  return {
    formId: NOVO_GRUPO_FORM_ID,
    tipos: {
      opcoes: elegiveis.map((tipo) => ({
        id: tipo.id,
        rotulo: `${tipo.codigo} · ${tipo.nome}`,
        marcado: values.tiposObraIds.includes(tipo.id),
        desabilitado: tipoTravado(tipo, elegiveis, values.tiposObraIds),
        alternar: () => alternarTipo(tipo.id),
      })),
      erro: form.errors.tiposObraIds ?? null,
    },
    nome: {
      valor: values.nome,
      max: NOME_MAXIMO,
      erro: duplicado ?? form.errors.nome ?? null,
      digitar: (valor) => {
        setDuplicado(null);
        form.setValue("nome", valor);
      },
    },
    subgrupos: secaoSubgrupos(elegiveis, values.tiposObraIds),
    isSubmitting: form.isSubmitting,
    submitError: form.submitError,
    enviar,
    cancelar: () => descarte.pedir(form.isDirty),
    descarte: descarte.confirmacao,
  };
}

function secaoSubgrupos(
  tipos: Parameters<typeof marcou103>[0],
  marcados: readonly string[],
): NovoGrupoDialogViewModel["subgrupos"] {
  if (marcou103(tipos, marcados)) {
    return { previa: SUBGRUPOS_DO_103, ajuda: SUBGRUPOS_AJUDA_103 };
  }
  return {
    previa: [],
    ajuda: marcados.length > 0 ? SUBGRUPOS_AJUDA_DOCAGEM : SUBGRUPOS_AJUDA_VAZIO,
  };
}
